<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "lesson-add",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      title: "",
      description: "",
      imageEntry: "",
      formSending: false,
      chapter: null,
      chapters: [],
    };
  },
  components: {
    Breadcrumb,
    InputText,
    Dropdown
  },
  validations() {
    return {
      title: { required },
      chapter: { required },
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/lessons`
          : `/lesson/${this.gradeSlug}`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [
          ...base,
          { name: 'الدروس', path: this.basePath },
          { name: 'أضافه درس', path: `${this.basePath}/add` },
        ];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'الدروس', path: this.basePath },
        { name: 'أضافه درس', path: `${this.basePath}/add` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() {
      return this.resolvedGrade?.id ?? null;
    },
  },
  methods: {
    sendForm() {
      this.v$.$validate();
      if (!this.v$.$error) {
        const formData = new FormData();
        formData.append('title', this.title);
        formData.append('description', this.description);
        if (this.isCourseScope) {
          formData.append('course_id', this.courseId);
        } else {
          formData.append('academic_year_id', this.gradeId);
        }
        formData.append('type', 3);
        formData.append('parent_id', this.chapter.id);
        this.formSending = true;

        axios
            .post('/lessons', formData)
            .then(() => {
              this.formSending = false;
              this.resetForm();
              Swal.fire({
                position: "center",
                icon: "success",
                title: "تم الاضافه بنجاح",
              });
            })
            .catch((error) => {
              this.formSending = false;
              Swal.fire({
                position: "center",
                icon: "error",
                title: error.response.data.message,
              });
            });
      }
    },
    resetForm() {
      this.v$.$reset();
      this.title = "";
      this.description = "";
      this.imageEntry = "";
    },
    fetchChapters() {
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId }
          : { type: 2, academic_year_id: this.gradeId };
      axios
          .get('/lessons', { params })
          .then((response) => {
            this.chapters = response.data.data;
            const chapterId = this.$route.query.chapter_id;
            if (chapterId) {
              this.chapter = this.chapters.find(c => String(c.id) === String(chapterId)) || null;
            }
          })
          .catch(() => {});
    },
    customLabelChapter(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchChapters();
  }
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-book"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة درس</h1>
        <p class="form-page-header__subtitle">إضافة درس جديد للمنهج الدراسي</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">التسلسل الهرمي</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label" for="chapter">الباب <span class="required">*</span></label>
                  <Dropdown v-model="chapter" :options="chapters" showClear :optionLabel="customLabelChapter" placeholder="اختر الباب" class="w-100" />
                  <span class="field-error" v-if="v$.chapter.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الباب
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">تفاصيل الدرس</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label" for="title">
                    اسم الدرس <span class="required">*</span>
                  </label>
                  <InputText id="title" v-model="title" placeholder="أدخل اسم الدرس" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الدرس
                  </span>
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label" for="description">
                    وصف الدرس <span class="optional">اختياري</span>
                  </label>
                  <InputText id="description" v-model="description" placeholder="أدخل وصف الدرس" class="w-100" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'حفظ الدرس' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
